import { useState } from 'react';
import { Button, InlineError, Input, Select, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import {
  QUICK_DATES,
  addDaysIso,
  followupErrorMessage,
  useCreateLeadFollowup,
} from './leadFollowups';

/**
 * Offered after an email is logged, and easy to walk past.
 *
 * ── Why dismissing is silent ───────────────────────────────────────────────
 *
 * No badge, no warning, no "you didn't set a follow-up". Kyu asked for
 * flexible judgement rather than a cadence, and a prompt that punishes
 * dismissal is a cadence wearing a different hat — people would start setting
 * dates they did not mean to clear the nag, and the next-action column would
 * fill with fiction. An empty Next action is a true statement about a lead
 * nobody has committed to chasing.
 *
 * ── Why the quick dates are buttons and the rest is behind one ─────────────
 *
 * Same shape the activity log settled on: the frequent case costs one click,
 * everything else is one click further. "Tomorrow" is the whole feature for
 * an inbound lead being worked daily; the owner picker and the note exist for
 * the times somebody is handing it over.
 */
export function FollowUpPrompt({
  leadId,
  todayIso,
  onDone,
}: {
  leadId: string;
  todayIso: string;
  /** Called on success or dismissal — the caller closes the prompt either way. */
  onDone: () => void;
}) {
  const { access } = useAuth();
  const me = access.status === 'approved' ? access.profile.id : '';
  const create = useCreateLeadFollowup();
  const people = useTapPeople(true);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  const [custom, setCustom] = useState('');
  const [assignee, setAssignee] = useState(me);
  const [note, setNote] = useState('');

  function set(due: string) {
    setError('');
    create.mutate(
      { leadId, due, assigneeId: assignee === '' ? null : assignee, note },
      {
        onSuccess: onDone,
        onError: (cause) => setError(followupErrorMessage(cause.message)),
      },
    );
  }

  return (
    <div className="lead-followup-prompt">
      {error !== '' && <InlineError>{error}</InlineError>}

      <p className="tracker-field-note">Set a follow-up?</p>

      <div className="lead-activity-actions">
        {QUICK_DATES.map((quick) => (
          <Button
            key={quick.key}
            size="sm"
            variant="secondary"
            disabled={create.isPending}
            onClick={() => set(addDaysIso(todayIso, quick.days))}
          >
            {quick.label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setMore(!more)}>
          {more ? 'Less' : 'Pick a date…'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Not now
        </Button>
      </div>

      {!more ? null : (
        <div className="lead-activity-form">
          <Input
            label="Date"
            type="date"
            value={custom}
            min={todayIso}
            onChange={(event) => setCustom(event.target.value)}
          />
          <Select
            label="Owner"
            value={assignee}
            onChange={(event) => setAssignee(event.target.value)}
            options={(people.data ?? []).map((person) => ({
              value: person.id,
              label: person.display_name === '' ? person.email : person.display_name,
            }))}
          />
          <Textarea
            label="Note (optional)"
            rows={2}
            value={note}
            placeholder="What are you following up about?"
            onChange={(event) => setNote(event.target.value)}
          />
          <Button
            size="sm"
            disabled={create.isPending || custom === ''}
            onClick={() => set(custom)}
          >
            {create.isPending ? 'Setting…' : 'Set follow-up'}
          </Button>
        </div>
      )}
    </div>
  );
}
